/* eslint-disable prettier/prettier */
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  MenuItem,
  usePrevious,
} from "@chakra-ui/react";
import { BatchItem } from "@rpldy/uploady";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDocumentList,
  useDocumentStatusUploadCompleteUpdate,
} from "gen/endpoints/documents/documents";
import {
  getInternalIntegrationTestingConfigurationRetrieveQueryKey,
  useInternalIntegrationTestingConfigurationRetrieveSuspense,
  useInternalIntegrationTestingConfigurationUpdate,
} from "gen/endpoints/integration-testing-configuration/integration-testing-configuration";
import { getInternalIntegrationTestingConfigurationTableRetrieveQueryKey } from "gen/endpoints/integration-testing-configuration-table/integration-testing-configuration-table";
import { DocumentCreateResponseData } from "gen/model/documentCreateResponseData";
import { DocumentStateMachineFieldsEnum } from "gen/model/documentStateMachineFieldsEnum";
import { DocumentTypeEnum } from "gen/model/documentTypeEnum";
import { IntegrationTestingConfigurationEditDataRequest } from "gen/model/integrationTestingConfigurationEditDataRequest";
import { IntegrationTestingConfigurationReadData } from "gen/model/integrationTestingConfigurationReadData";
import { IntegrationTestVersionTypeEnum } from "gen/model/integrationTestVersionTypeEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { compact, get } from "lodash-es";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import { FormBooleanToggleButtonGroup } from "shared/components/button/ToggleButton";
import {
  ContentBlock,
  ContentBlockActions,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockMenu,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import { DocumentList } from "shared/components/document/DocumentList";
import { FormDrawer } from "shared/components/form/FormDrawer";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { DocumentDownloadLink } from "shared/components/link/DownloadDocumentLink";
import { FileUpload } from "shared/components/upload/FileUpload";
import { FileUploadHandle } from "shared/components/upload/FileUploadController";
import { uploadAcceptJson } from "shared/components/upload/FileUploadUtils";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export interface InternalIntegrationTestingConfigurationDrawerFormProps {
  configurationData: IntegrationTestingConfigurationReadData;
  onCloseComplete: () => void;
}

export const InternalIntegrationTestingConfigurationEditDrawer: React.FC<
  InternalIntegrationTestingConfigurationDrawerFormProps
> = ({ configurationData, onCloseComplete }) => {
  const { t } = useTranslation(["common", "internal"]);
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const form = useForm<IntegrationTestingConfigurationEditDataRequest>({
    defaultValues: {
      enabled: configurationData.enabled || false,
      export_enabled: configurationData.export_enabled || false,
      template_document_id: configurationData.template_document_id,
    },
  });
  const uploadRef = useRef<FileUploadHandle>(null);

  const { mutateAsync: editConfiguration } =
    useInternalIntegrationTestingConfigurationUpdate({
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey:
              getInternalIntegrationTestingConfigurationTableRetrieveQueryKey(),
          });
        },
      },
    });

  const { mutateAsync: documentStatusUploadCompleteUpdate } =
    useDocumentStatusUploadCompleteUpdate();

  const documentId = form.watch("template_document_id");
  const { data: documentListData } = useDocumentList(
    { ids: compact([configurationData.template_document_id, documentId]) },
    {
      query: {
        enabled: !!configurationData.template_document_id || !!documentId,
        refetchInterval: 1000,
      },
    },
  );

  const onItemsChanged = useCallback(
    (items: BatchItem[]) => {
      if (items.length === 0) {
        form.setValue("template_document_id", null);
      }
    },
    [form],
  );

  const onItemFinished = async (item: BatchItem) => {
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    const document: DocumentCreateResponseData = get(item, "document")!;
    await documentStatusUploadCompleteUpdate({ id: document.id });
    setErrorMessage(undefined);
    form.setValue("template_document_id", document.id);
  };

  const documentData =
    documentListData && documentListData.length == 1
      ? documentListData[0]
      : undefined;
  const prevState = usePrevious(documentData?.state);
  useEffect(() => {
    if (documentData && prevState !== documentData.state) {
      if (
        documentData.state !== DocumentStateMachineFieldsEnum.upload_succeeded
      ) {
        uploadRef.current?.clear();
        setErrorMessage(documentData.error);
        form.setValue("template_document_id", null);
      }
    }
  }, [documentData, prevState, onCloseComplete, form]);

  return (
    <FormDrawer
      title={t("internal:integrationTesting.configuration.title")}
      formProps={{
        form,
        onSubmit: async (data) =>
          await editConfiguration({
            integrationTestingConfigurationId: configurationData.id,
            data,
          }),
        successMessage: t(
          "internal:integrationTesting.configuration.edit.success",
        ),
        objectDisplayName: `${configurationData.customer_name} - ${configurationData.version_type}`,
      }}
      onCloseComplete={onCloseComplete}
      bodyProps={{
        "data-private": true,
      }}
    >
      {errorMessage && (
        <Alert
          status="error"
          mt="16px"
          data-ls-enabled={true}
          data-public={true}
        >
          <AlertIcon />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      <FormBooleanToggleButtonGroup
        name="enabled"
        title={t("common:enabled")}
      />
      <FormBooleanToggleButtonGroup
        name="export_enabled"
        isDisabled={
          configurationData.version_type == IntegrationTestVersionTypeEnum.v2
        }
        title={t(
          "internal:integrationTesting.configuration.form.exportEnabled",
        )}
      />
      {!documentData && (
        <Box mb="16px">
          <FileUpload
            documentType={DocumentTypeEnum.event_template}
            accept={uploadAcceptJson}
            onItemFinished={onItemFinished}
            onItemsChanged={onItemsChanged}
            handleRef={uploadRef}
          />
        </Box>
      )}
      <DocumentList
        documents={compact([documentData])}
        onDocumentDeleted={() => form.setValue("template_document_id", null)}
      />
    </FormDrawer>
  );
};

export const InternalIntegrationTestingConfigurationPage: React.FC = () => {
  const { t } = useTranslation(["internal", "common"]);
  const queryClient = useQueryClient();
  const { integrationTestingConfigurationId } = useRequiredStringParams();

  const { data: configurationData } =
    useInternalIntegrationTestingConfigurationRetrieveSuspense(
      integrationTestingConfigurationId,
    );

  const [isEditing, setEditing] = useState<boolean>(false);

  const onCloseComplete = useCallback(async () => {
    setEditing(false);
    await queryClient.invalidateQueries({
      queryKey: getInternalIntegrationTestingConfigurationRetrieveQueryKey(
        configurationData.id,
      ),
    });
  }, [configurationData, queryClient]);

  return (
    <PageContent>
      <PageHeader
        title={t("internal:integrationTesting.configuration.title")}
        backButton={{
          title: t("internal:integrationTesting.title"),
          path: rootRoute({})
            .internal({})
            .integrationTesting({})
            .configurations({}).$,
        }}
      />
      <PageBody>
        {isEditing && (
          <InternalIntegrationTestingConfigurationEditDrawer
            configurationData={configurationData}
            onCloseComplete={onCloseComplete}
          />
        )}
        <ContentBlockGroup>
          <ContentBlock>
            <ContentBlockHeader>
              <ContentBlockActions>
                <Restricted permission={PermissionTypeEnum.config}>
                  <ContentBlockMenu>
                    <MenuItem
                      onClick={() => {
                        setEditing(true);
                      }}
                    >
                      {t("common:edit")}
                    </MenuItem>
                  </ContentBlockMenu>
                </Restricted>
              </ContentBlockActions>
            </ContentBlockHeader>
            <ContentBlockBody variant="descriptions">
              <Descriptions>
                <Descriptions.Item
                  label={t(
                    "internal:integrationTesting.configuration.form.integrationTestVersion",
                  )}
                >
                  {configurationData.version_type}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    "internal:integrationTesting.configuration.form.customerName",
                  )}
                >
                  {configurationData.customer_name}
                </Descriptions.Item>
                {configurationData.shadow_path && (
                  <Descriptions.Item
                    label={t(
                      "internal:integrationTesting.configuration.form.templatePath",
                    )}
                  >
                    {configurationData.shadow_path}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={t("common:enabled")}>
                  {configurationData.enabled}
                </Descriptions.Item>
                <Descriptions.Item
                  label={t(
                    "internal:integrationTesting.configuration.form.exportEnabled",
                  )}
                >
                  {configurationData.export_enabled}
                </Descriptions.Item>
                {configurationData.template_document_id && (
                  <Descriptions.Item
                    label={t(
                      "internal:integrationTesting.configuration.form.template",
                    )}
                  >
                    <DocumentDownloadLink
                      documentId={configurationData.template_document_id}
                    >
                      {t("common:download")}
                    </DocumentDownloadLink>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </ContentBlockBody>
          </ContentBlock>
        </ContentBlockGroup>
      </PageBody>
    </PageContent>
  );
};
